import * as Sentry from '@sentry/react';
import { useIdentity } from 'app/core/api/company/api-company';
import { contextActions } from 'app/core/redux/context-redux';
import {
  useAuthToken,
  useClearDataOnMissingAuthToken,
} from 'app/core/token/token-utils';
import { PropsOf } from 'app/tools/general.types';
import { createBrowserHistory } from 'history';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  RouteProps,
  useLocation,
} from 'react-router-dom';
import { Except } from 'type-fest';
import {
  PageTitleProvider,
  usePageTitle,
} from '../context-hooks/use-titlebar.hook';
import BottomNav from '../page-parts/BottomNav';
import HeaderBar from '../page-parts/HeaderBar';
import SharedRoutePath from '../SharedRoutePath';
import { createPreserveQueryHistory } from './browserHistory';
import ViewFrame from './ViewFrame';

export const routerHistory = createPreserveQueryHistory(
  createBrowserHistory,
  ['event']
)();

const SentryRoute = Sentry.withSentryRouting(Route);

type Props = Except<
  PropsOf<Route>,
  'children' | 'component' | 'render'
> & {
  display: RouteProps['children'];
  title?: string;
  publicRoute?: boolean;
  /**
   * Whether to show the header / footer or not. true = hide both,
   * false = show both, 'show<Footer|Header>' = show only that one
   * */
  fullScreen?: boolean | 'showHeader' | 'showFooter';
  noSideNavItems?: boolean;
  backButton?: boolean;
  /**
   * If true, don't pad sides of frame. This will be handled by the
   * page component instead
   * */
  edgeToEdge?: boolean;
  centerContent?: boolean;
  soloContent?: boolean;
  cardBgColor?: boolean;
};

export function AppRouteInner({
  display,
  title,
  publicRoute,
  fullScreen = false,
  noSideNavItems,
  backButton,
  edgeToEdge,
  soloContent,
  centerContent,
  cardBgColor,
  ...props
}: Props): JSX.Element {
  const location = useLocation();
  const dispatch = useDispatch();
  const authToken = useAuthToken();

  useClearDataOnMissingAuthToken();

  const { title: titleAssigned, altHeader } = usePageTitle();
  const navActions = useSelector(s => s.context.navActions);
  const sideNavItems = useSelector(s => s.context.sideMenuNavItems);
  const menuOpen = useSelector(s => s.context.menuOpen);

  const { resource: { user } = {}, request: fetchIdentity } =
    useIdentity({ noAutoFetch: true });

  const { email, firstName, lastName } = user || {};
  useEffect(() => {
    if (email) {
      Sentry.setUser({
        email,
        username: `${firstName || ''} ${lastName?.[0] || ''}`.trim(),
      });
    }
  }, [email, firstName, lastName]);

  useEffect(() => {
    dispatch(contextActions.fullScreen.set(fullScreen));
  }, [fullScreen, dispatch]);

  useEffect(() => {
    if (authToken) fetchIdentity();
  }, [authToken, fetchIdentity]);

  const header = altHeader || (
    <HeaderBar
      title={titleAssigned || title}
      backButton={publicRoute || backButton}
      noLogo={publicRoute}
    />
  );

  useEffect(() => {
    if (!authToken && menuOpen) {
      dispatch(contextActions.menuOpen.set(false));
    }
  }, [authToken, dispatch, menuOpen]);

  useEffect(() => {
    if (noSideNavItems) {
      dispatch(contextActions.sideMenuNavItems.set(false));
    } else if (!sideNavItems) {
      dispatch(contextActions.sideMenuNavItems.set(true));
    }
  });

  return (
    <SentryRoute {...props}>
      {authToken || publicRoute ? (
        <ViewFrame
          header={
            (!fullScreen || fullScreen === 'showHeader') && header
          }
          footer={
            (!fullScreen || fullScreen === 'showFooter') &&
            !publicRoute && <BottomNav {...{ navActions }} />
          }
          {...{
            edgeToEdge,
            soloContent,
            centerContent,
            cardBgColor,
          }}
        >
          {display}
        </ViewFrame>
      ) : (
        <Redirect
          to={{
            pathname: SharedRoutePath.LOGIN,
            state: { from: location },
          }}
        />
      )}
    </SentryRoute>
  );
}

export default function AppRoute(props: Props): JSX.Element {
  return (
    <PageTitleProvider>
      <AppRouteInner {...props} />
    </PageTitleProvider>
  );
}
